// FT-IMPORT Story 2 — pure logic for the Fast Track "import a GitHub issue"
// picker: the GH-issue → Fast Track field mapping plus the two soft heuristics
// (multi-scope nudge, thin-issue prompt). Kept UI-free so it's unit-testable in
// isolation and the picker component stays presentational.
//
// The mapping mirrors S1's backend mapGithubIssueToFastTrack (apps/api) — the
// backend is the source of truth for what gets persisted, but the picker also
// maps client-side to pre-fill + preview the form BEFORE the create POST. Keep
// the two in sync; they are deliberately thin (title/body/label only).

export type FastTrackLabel = 'bug' | 'hotfix' | 'security';

// Mirrors FAST_TRACK_LABELS in apps/api routes/projects.ts + the modal's
// LABEL_OPTIONS. Tiny + stable; promote to packages/shared if a 4th home appears.
export const FAST_TRACK_LABELS: readonly FastTrackLabel[] = ['bug', 'hotfix', 'security'];

// Shapes returned by S1's GET /github/repos and /github/repos/:owner/:repo/issues.
export interface GithubRepoSummary {
  fullName:  string;   // "owner/repo"
  owner:     string;
  name:      string;
  private:   boolean;
  updatedAt: string | null;
}

export interface GithubIssueSummary {
  number:   number;
  title:    string;
  body:     string | null;
  labels:   string[];
  comments: number;
  htmlUrl:  string;
}

export interface MappedFastTrackFields {
  title:       string;
  description: string;
  /** Set only when a GH label matches a FAST_TRACK category; else omitted so
   *  the caller keeps the user's current label choice. */
  label?: FastTrackLabel;
}

/**
 * GH issue → Fast Track fields. title→title, body→description (null→''), and a
 * matching FAST_TRACK label (case-insensitive) → label, else label omitted.
 */
export function mapGithubIssueToFastTrack(
  issue: Pick<GithubIssueSummary, 'title' | 'body' | 'labels'>,
): MappedFastTrackFields {
  // Mirror S1's backend mapGithubIssueToFastTrack EXACTLY: the first GH label
  // (in the issue's own label order) that is a FAST_TRACK category wins — not a
  // fixed bug/hotfix/security priority. Keeping the selection identical is what
  // guarantees the client preview equals what the backend would map/persist.
  const label = issue.labels
    .map(l => l.toLowerCase())
    .find((l): l is FastTrackLabel => (FAST_TRACK_LABELS as readonly string[]).includes(l));
  return {
    title:       issue.title,
    description: issue.body ?? '',
    ...(label ? { label } : {}),
  };
}

// ── Heuristics ──────────────────────────────────────────────────────────────
// Both are SOFT nudges, never blocks. Tuned for signal-not-noise; thresholds
// are exported so tests pin the boundary and they can be tuned in one place.

export const MULTI_SCOPE_COMMENT_THRESHOLD = 10;
export const MULTI_SCOPE_UNCHECKED_THRESHOLD = 5;
export const THIN_BODY_MAX_LENGTH = 30;

// Labels that signal a tracking/umbrella issue rather than a single unit of work.
export const EPIC_LABELS: readonly string[] = ['epic', 'tracking', 'meta', 'roadmap', 'theme'];

/**
 * Count of unchecked markdown checklist items (`- [ ]`) in the body. Anchored to
 * line start (tolerating indentation) so nested sub-task checklists count, and
 * checked items (`- [x]`) never match. `m` flag makes `^` match each line.
 */
export function countUncheckedBoxes(body: string | null): number {
  if (!body) return 0;
  return (body.match(/^[ \t]*- \[ \]/gm) ?? []).length;
}

export interface MultiScopeSignal {
  isMultiScope: boolean;
  reasons:      string[];
}

/**
 * Fast Track is single-issue. Fire a soft "this looks multi-scope" nudge when
 * the issue carries an epic/tracking label, OR has many comments (active
 * multi-party thread), OR a long unchecked checklist (umbrella of sub-tasks).
 */
export function detectMultiScope(
  issue: Pick<GithubIssueSummary, 'labels' | 'comments' | 'body'>,
): MultiScopeSignal {
  const reasons: string[] = [];
  const lower = issue.labels.map(l => l.toLowerCase());
  if (lower.some(l => EPIC_LABELS.includes(l))) {
    reasons.push('it carries an epic/tracking label');
  }
  if (issue.comments >= MULTI_SCOPE_COMMENT_THRESHOLD) {
    reasons.push(`it has ${issue.comments} comments`);
  }
  const unchecked = countUncheckedBoxes(issue.body);
  if (unchecked >= MULTI_SCOPE_UNCHECKED_THRESHOLD) {
    reasons.push(`it has ${unchecked} unchecked checklist items`);
  }
  return { isMultiScope: reasons.length > 0, reasons };
}

/**
 * A GH issue with little/no body gives Sprint Planning nothing to work from.
 * Surface the same concern the planning agent would raise — earlier, at import.
 */
export function isThinIssue(issue: Pick<GithubIssueSummary, 'body'>): boolean {
  return (issue.body ?? '').trim().length < THIN_BODY_MAX_LENGTH;
}
